import { Directive, effect, inject } from '@angular/core';
import { NzDatePickerComponent } from 'ng-zorro-antd/date-picker';

@Directive()
export abstract class BaseLocalizedFormatDirective {
  protected readonly picker = inject(NzDatePickerComponent);

  private hasThrownForManualNzFormat = false;
  protected didApplyIsCustomFormatHack = false;

  protected abstract readonly directiveName: string;

  constructor() {
    effect(() => {
      this.runEffectLogic();
    });
  }

  private runEffectLogic(): void {
    const mode = this.picker.nzMode;
    this.checkMode(mode);

    const pickerAny = this.picker as unknown as {
      isCustomFormat?: boolean;
    };

    const manualNzFormatDetected =
      !!pickerAny.isCustomFormat && !this.didApplyIsCustomFormatHack;
    const shouldThrowForManualNzFormat =
      manualNzFormatDetected && !this.hasThrownForManualNzFormat;

    this.applyCustomFormatEffects();

    // This is a small hack to ensure ng-zorro treats the format as user/custom, so it doesn't overwrite it
    // with the default in `setModeAndFormat()`.
    // It is not harmful if this is not set but it is better to guarantee that we don't have flaky behavior
    (this.picker as unknown as { isCustomFormat: boolean }).isCustomFormat =
      true;
    this.didApplyIsCustomFormatHack = true;

    // Refresh display + parsing validation for the new format.
    this.picker.updateInputValue();

    if (shouldThrowForManualNzFormat) {
      this.hasThrownForManualNzFormat = true;
      throw new Error(
        `Do not set nzFormat manually on nz-date-picker/nz-range-picker when using ${this.directiveName}. Remove nzFormat and let the directive apply it.`,
      );
    }

    this.throwAdditionalErrors();
  }

  /**
   * Validate the `nzMode` of the picker.
   * Override this to check for specific modes and throw errors if the mode is unsupported.
   * @param mode The current nzMode of the picker (can be undefined).
   */
  protected abstract checkMode(mode?: string): void;

  /**
   * Apply any directive-specific formats to the picker (e.g. nzFormat, nzShowTime).
   * Note: Signals read here will be tracked by the base effect.
   */
  protected abstract applyCustomFormatEffects(): void;

  /**
   * Throw any additional errors after the picker inputValue has updated.
   * Override this if the subclass needs to throw specific errors.
   */
  protected throwAdditionalErrors(): void {
    // Default: no-op
  }
}
